// Standard library imports
import React, { useState, useEffect } from 'react';

// External library import
import { UilTrash, UilPen, UilTimes, UilCheck, UilImport, UilFileAlt, UilFileDownload, UilTimesCircle } from '@iconscout/react-unicons';
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';

// Internal module imports
import Typography from '../../../../components/Typography/Typography';
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import Table from '../../../../components/Table/Table';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import { TextArea } from '../../../../components/Inputs/Input';
import Dropdown from '../../../../components/Inputs/Dropdown';
import { incidentValidation } from '../../../../validations/Edge/incidentValidation';
import { IncidentsServiceRoot } from '../../../../services/EdgeIncidentService';
import { Capitalize } from '../../../../utils/stringHelper';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';

// CSS Imports
import classes from '../../../Edge/EdgeError/EdgeError.module.css';
import bidClasses from '../../../../styles/Bids.module.css';
import bidsClasses from '../../../../styles/BidModal.module.css';
import monitoringClasses from '../Monitoring.module.css';
import incidentClasses from '../../../Edge/EdgeIncidents/EdgeIncidents.module.css';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import { AdminService } from '../../../../services/AdminService';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import EdgeIncidentFileUploadHistory from './EdgeIncidentFileUploadHistory';
import MultipleFilesUpload from '../../../../components/Inputs/FileUpload/MultipleFileUpload';
import { StringParam } from 'use-query-params';

const typeOptions = [
    { label: 'External', value: 'external' },
    { label: 'Internal', value: 'internal' },
    { label: 'Deployment', value: 'deployment' },
];

const statusOptions = [
    { label: 'Open', value: 'opened' },
    { label: 'Aknowledge', value: 'acknowledged' },
    { label: 'Close', value: 'closed' },
];

const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
];

const defaultValue = {
    label: 'All',
    value: 'all',
};

const LIMIT = 15;

const EdgeIncidents = ({ admins, cId }) => {
    const [startLoader, stopLoader] = useLoader();
    const [totalCount, setTotalCount] = useState(0);

    const [tableData, setTableData] = useState([]);
    const [createIncidentModal, setCreateIncidentModal] = useState({ status: false, data: {} });
    const [deleteModal, setDeleteModal] = useState({ status: false, id: '' });

    const [selectedAdmin, setSelectedAdmin] = useQueryParamWithDefault('admin', StringParam, defaultValue.value, cId);
    const [selectedCustomer, setSelectedCustomer] = useQueryParamWithDefault('customer', StringParam, defaultValue.value, cId);
    const [customers, setCustomers] = useState([defaultValue]);
    const [edgeOptions, setEdgeOptions] = useState([defaultValue]);
    const [selectedEdge, setSelectedEdge] = useQueryParamWithDefault('edge', StringParam, defaultValue.value, cId);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalPageCount, setTotalPageCount] = useState(0);

    const [files, setFiles] = useState([]);
    const [incidentFileListModal, setIncidentFileListModal] = useState({ status: false, data: {} });

    useEffect(() => {
        if (admins?.length) setSelectedAdmin(selectedAdmin ? selectedAdmin : admins[0].value);
    }, [admins]);

    useEffect(() => {
        if (selectedAdmin && selectedAdmin !== 'all') getCustomers();

        setCustomers([defaultValue]);
        setSelectedCustomer(defaultValue.value);
        setEdgeOptions([defaultValue]);
        setSelectedEdge(defaultValue.value);
    }, [selectedAdmin]);

    useEffect(() => {
        if (selectedCustomer && selectedCustomer !== 'all') getEdges();

        setEdgeOptions([defaultValue]);
        setSelectedEdge(defaultValue.value);
    }, [selectedCustomer]);

    useEffect(() => {
        getEdgeIncidents();
    }, [selectedAdmin, selectedCustomer, selectedEdge, skip]);

    const getCustomers = () => {
        AdminService.GetCustomers(selectedAdmin, startLoader, handleGetCustomerSuccess, handleError, stopLoader);
    };

    const getEdges = () => {
        AdminService.GetEdges(selectedCustomer, startLoader, handleGetEdgesSuccess, handleError, stopLoader);
    };

    const handleGetEdgesSuccess = ({ data }) => {
        let temp = [defaultValue];
        data.data.forEach((c) => {
            temp.push({
                label: c.edgeAlias,
                value: c.deviceId,
            });
        });
        setEdgeOptions(temp);
    };

    const handleGetCustomerSuccess = ({ data }) => {
        let temp = [defaultValue];
        data.data.forEach((c) => {
            temp.push({
                label: c.name,
                value: c._id,
            });
        });
        setCustomers(temp);
    };

    const getEdgeIncidents = () => {
        if (selectedAdmin && selectedCustomer && selectedEdge) {
            const params = {
                adminId: selectedAdmin,
                customerId: selectedCustomer,
                deviceId: selectedEdge,
                skip,
                limit: LIMIT,
            };
            IncidentsServiceRoot.GetIncidents(params, startLoader, handleEdgeIncidentsSuccess, handleError, stopLoader);
        }
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    const handleDownload = () => {
        let data = tableData.map((i) => {
            const item = i.item;
            return {
                edgeAlias: item.edgeAlias ? item.edgeAlias : '--',
                status: item.status ? Capitalize(item.status) : '--',
                priority: item.priority,
                type: item.type ? Capitalize(item.type) : '--',
                notes: item.notes,
                marketStatus: item.marketStatus,
                openedAt: item.createdAt ? momentTimeFormater(item.createdAt).format('YYYY-MM-DD HH:mm') : '--',
            };
        });
        DownloadAsExcel(data, `Edge Incident Logs`, ['Edge Alias', 'Status', 'Priority', 'Type', 'Notes', 'Market Status', 'Opened At (CET)']);
    };

    const handleEdgeIncidentsSuccess = ({ data }) => {
        const items = data.data?.data || [];
        const count = data.data?.count || 0;
        const processedData = items.map((item, index) => {
            const notes = item.notes;
            return {
                item,
                slNo: index + 1 + skip,
                status: item.status ? Capitalize(item.status) : '--',
                priority: (
                    <div className={classes.DataMonitoringContainer}>
                        {item.priority === 'low' ? (
                            <div className={classes.Low}></div>
                        ) : item.priority === 'high' ? (
                            <div className={classes.High}></div>
                        ) : (
                            <div className={classes.Medium}></div>
                        )}
                    </div>
                ),
                type: item.type ? Capitalize(item.type) : '--',
                notes: notes.length > 35 ? <CustomTooltip content={notes}>{notes.substring(0, 35) + '...'}</CustomTooltip> : notes || '--',
                marketStatus: item.marketStatus ? <UilCheck size="1.5vw" style={{ color: 'green' }} /> : <UilTimes size="1.5vw" style={{ color: 'red' }} />,
                openedAt: item.createdAt ? momentTimeFormater(item.createdAt).format('YYYY-MM-DD HH:mm') : '--',
                edgeAlias: item.edgeAlias ? item.edgeAlias : '--',
                action: (
                    <div
                        style={{
                            display: 'flex',
                            gridColumnGap: '1vw',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CustomTooltip content={'Edit'}>
                            <UilPen
                                size={'1.2vw'}
                                style={{ color: 'var(--color-primary)' }}
                                onClick={() => {
                                    setCreateIncidentModal({ status: true, data: item });
                                    setFiles(Array.isArray(item.files) ? item.files : []);
                                }}
                            />
                        </CustomTooltip>
                        <CustomTooltip content={'View files'}>
                            <UilFileAlt size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => setIncidentFileListModal({ status: true, data: item })} />
                        </CustomTooltip>
                        <CustomTooltip content={'Delete'}>
                            <UilTrash size={'1.2vw'} style={{ color: 'var(--color-primary)' }} onClick={() => setDeleteModal({ status: true, id: item._id })} />
                        </CustomTooltip>
                    </div>
                ),
            };
        });
        setTableData(processedData);
        setTotalCount(count);
        setTotalPageCount(count);
    };

    const handleSubmit = (values) => {
        if (createIncidentModal.data?.closedAt) {
            values.closedAt = createIncidentModal.data?.closedAt;
        }

        if (files?.length) {
            values.files = files;
        }

        IncidentsServiceRoot.Update(createIncidentModal.data?._id, values, startLoader, () => handleSuccess('update'), handleError, stopLoader);
    };

    const handleSuccess = (type) => {
        getEdgeIncidents();
        setCreateIncidentModal({ status: false, data: {} });
        toast.success(`Incident ${type} successfully`);
    };

    const handleDelete = () => {
        IncidentsServiceRoot.Delete(deleteModal.id, startLoader, handleDeleteSuccess, handleError, stopLoader);
    };

    const handleDeleteSuccess = (res) => {
        getEdgeIncidents();
        setDeleteModal({ status: false, id: '' });
        toast.success('Incident deleted successfully');
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    const handleFilesSelect = (e) => {
        const files = e.target.files;
        uploadFiles(files);
    };

    const uploadFiles = (files) => {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }

        IncidentsServiceRoot.UploadFiles(createIncidentModal.data?._id, formData, startLoader, handleUploadFilesSuccess, handleError, stopLoader);
    };

    const handleUploadFilesSuccess = ({ data }) => {
        setFiles((prev) => [...prev, ...data.data]);
    };

    const removeFile = (key, id) => {
        IncidentsServiceRoot.RemoveIncidentUploadFile(id, startLoader, handleRemoveFileSuccess, handleError, stopLoader);
        const temp = files.filter((e) => e.key !== key);
        setFiles(temp);
    };

    const handleRemoveFileSuccess = () => {
        toast.success('File removed successfully');
    };

    return (
        <div>
            <div className={monitoringClasses.Filter}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className={monitoringClasses.FilterContainer}>
                        <div>
                            <div className={bidsClasses.FieldControl}>
                                <label>Select Admin</label>
                                <DropdownComponent
                                    name="adminId"
                                    options={admins}
                                    defaultValue={{ value: selectedAdmin }}
                                    onChange={(e) => {
                                        setSelectedAdmin(e.value);
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <div className={bidsClasses.FieldControl}>
                                <label>Select Customer</label>
                                <DropdownComponent
                                    name="customerId"
                                    options={customers}
                                    defaultValue={{ value: selectedCustomer }}
                                    onChange={(e) => {
                                        setSelectedCustomer(e.value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className={bidsClasses.FieldControl} style={{ minWidth: '10vw' }}>
                            <label>Edge</label>
                            <DropdownComponent
                                name="filter"
                                options={edgeOptions}
                                defaultValue={{ value: selectedEdge }}
                                onChange={(e) => {
                                    setSelectedEdge(e.value);
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', paddingRight: '1vw' }}>
                        <CustomTooltip content={'Download'}>
                            <div className={classes.Action} onClick={() => handleDownload()}>
                                <div>
                                    <UilImport size={'1.2vw'} style={{ color: 'var(--color-primary)', cursor: 'pointer' }} />
                                </div>
                            </div>
                        </CustomTooltip>
                    </div>
                </div>
            </div>

            <div className={bidClasses.Bids}>
                <div className={classes.Header}>
                    <div className={classes.ErrorDropdownContainer}>
                        <Typography content={'Edge Incidents'} />
                        <div className={bidClasses.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={totalCount || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <Table
                        head={['Sl.No', 'EDGE ALIAS', 'STATUS', 'PRIORITY', 'TYPE', 'NOTES', 'MARKET STATUS', 'OPENED AT (CET)', 'ACTION']}
                        keys={['slNo', 'edgeAlias', 'status', 'priority', 'type', 'notes', 'marketStatus', 'openedAt', 'action']}
                        data={tableData}
                        page={page}
                        Pagination={true}
                        limit={LIMIT}
                        handlePageChange={handlePageChange}
                        totalCount={totalPageCount}
                    />
                </div>

                <ModalComponent isOpen={createIncidentModal.status} setOpen={() => setCreateIncidentModal({ status: false, data: '' })}>
                    <div style={{ minWidth: '50vw' }}>
                        <Typography content={createIncidentModal.data?._id ? 'Update Incident' : 'Create Incident'} />

                        <Formik
                            initialValues={{
                                status: createIncidentModal.data?.status ? createIncidentModal.data?.status : statusOptions[0]?.value,
                                priority: createIncidentModal.data?.priority ? createIncidentModal.data?.priority : priorityOptions[0]?.value,
                                type: createIncidentModal.data?.type ? createIncidentModal.data?.type : typeOptions[0]?.value,
                                notes: createIncidentModal.data?.notes ? createIncidentModal.data?.notes : '',
                            }}
                            enableReinitialize
                            validationSchema={incidentValidation}
                            onSubmit={(val) => {
                                handleSubmit(val);
                            }}
                        >
                            {({ errors, touched, values, isValidating, ...props }) => (
                                <Form>
                                    <div>
                                        <div>
                                            <div className={bidsClasses.InputContainer}>
                                                <div className={bidsClasses.FieldControl2}>
                                                    <label for="status">
                                                        Status <span className="required">*</span>
                                                    </label>
                                                    <Dropdown
                                                        name="status"
                                                        options={statusOptions}
                                                        defaultValue={createIncidentModal.data?.status ? { value: createIncidentModal.data?.status } : statusOptions[0]}
                                                    />
                                                </div>
                                                <div className={bidsClasses.FieldControl2}>
                                                    <label for="priority">
                                                        Priority <span className="required">*</span>
                                                    </label>
                                                    <Dropdown
                                                        name="priority"
                                                        options={priorityOptions}
                                                        defaultValue={createIncidentModal.data?.priority ? { value: createIncidentModal.data?.priority } : priorityOptions[0]}
                                                    />
                                                </div>
                                            </div>
                                            <div className={bidsClasses.InputContainer}>
                                                <div className={bidsClasses.FieldControl2}>
                                                    <label for="type">
                                                        Type <span className="required">*</span>
                                                    </label>
                                                    <Dropdown
                                                        name="type"
                                                        options={typeOptions}
                                                        defaultValue={createIncidentModal.data?.type ? { value: createIncidentModal.data?.type } : typeOptions[0]}
                                                    />
                                                </div>
                                            </div>
                                            <div className={bidsClasses.InputContainer}>
                                                <div className={bidsClasses.FieldControl2}>
                                                    <label for="notes">
                                                        Notes <span className="required">*</span>
                                                    </label>
                                                    <TextArea name={`notes`} id="notes" type="text" />
                                                </div>
                                            </div>

                                            <div className={bidsClasses.InputContainer}>
                                                <div className={bidsClasses.FieldControl2} style={{ width: '99.7%' }}>
                                                    <label for="uploadFiles">Files</label>
                                                    <div>
                                                        <MultipleFilesUpload name="uploadFiles" id="uploadFiles" accept="*" multiple onChange={handleFilesSelect} />
                                                    </div>
                                                </div>
                                            </div>

                                            {files?.length ? (
                                                <div className={bidClasses.UploadedEdgeFiles}>
                                                    {files.map((item, index) => (
                                                        <div>
                                                            <div className={incidentClasses.SelectedFile}>
                                                                <div key={index}>
                                                                    <p>{item.name}</p>
                                                                </div>
                                                                <div style={{ display: 'flex' }}>
                                                                    <div className={incidentClasses.CloseIcon}>
                                                                        <UilFileDownload
                                                                            style={{
                                                                                color: 'var(--color-primary)',
                                                                            }}
                                                                            size={'1.3vw'}
                                                                        />
                                                                    </div>
                                                                    <div onClick={() => removeFile(item.key, item._id)} className={incidentClasses.CloseIcon}>
                                                                        <UilTimesCircle
                                                                            style={{
                                                                                color: 'var(--color-primary)',
                                                                            }}
                                                                            size={'1.3vw'}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                ''
                                            )}

                                            <div className={bidsClasses.ButtonContainer}>
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn-secondary"
                                                        onClick={(e) => {
                                                            setCreateIncidentModal({ status: false, data: '' });
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                                <div>
                                                    <button className="btn-primary" type="submit">
                                                        Submit
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </ModalComponent>

                <ModalComponent isOpen={incidentFileListModal.status} setOpen={() => setIncidentFileListModal({ status: false, data: '' })}>
                    <div style={{ minWidth: '50vw' }}>
                        <EdgeIncidentFileUploadHistory incidentId={incidentFileListModal.data?._id} getEdgeIncidents={getEdgeIncidents} />
                    </div>
                </ModalComponent>

                <DeleteModal
                    deletefunction={handleDelete}
                    opendeleteModal={deleteModal.status}
                    setOpenDeleteModal={(status) => setDeleteModal({ ...deleteModal, status })}
                ></DeleteModal>
            </div>
        </div>
    );
};

export default EdgeIncidents;
